import React, { useState, useCallback, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Rating,
  Fade
} from '@mui/material';
import {
  AutoFixHigh,
  ExpandMore,
  ExpandLess,
  Lightbulb,
  AutoAwesome,
  Translate,
  FormatQuote,
  TrendingUp,
  Edit,
  ContentCopy,
  Check,
  Close,
  Refresh,
  KeyboardArrowLeft,
  ThumbUp,
  ThumbDown,
  Spellcheck,
  Psychology,
  Summarize,
  SwapHoriz,
  Visibility,
  VisibilityOff,
  History,
  FormatBold,
  FormatItalic,
  FormatUnderlined
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';

const WritingAssistant = ({ selectedText, onSuggestion, onApplySuggestion, theme }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [assistantMode, setAssistantMode] = useState('improve');
  const [customPrompt, setCustomPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [editedResponse, setEditedResponse] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [currentAction, setCurrentAction] = useState('');
  const [actionHistory, setActionHistory] = useState([]);
  const [confidence, setConfidence] = useState(0);
  const [showFormatting, setShowFormatting] = useState(false);
  const editableRef = useRef(null);

  // Streamlined assistant modes focused on core writing needs
  const assistantModes = [
    {
      id: 'improve',
      label: '✨ Enhance',
      icon: <AutoAwesome />,
      description: 'Make it clearer and more impactful',
      color: theme.primary,
      shortcut: 'E',
      endpoint: '/api/writing-assistant/improve'
    },
    {
      id: 'grammar',
      label: '📝 Fix Grammar',
      icon: <Spellcheck />,
      description: 'Correct grammar and spelling',
      color: '#10b981',
      shortcut: 'G',
      endpoint: '/api/writing-assistant/fix-grammar'
    },
    {
      id: 'tone',
      label: '🎯 Adjust Tone',
      icon: <Psychology />,
      description: 'Make it more professional',
      color: '#8b5cf6',
      shortcut: 'T',
      endpoint: '/api/writing-assistant/adjust-tone'
    },
    {
      id: 'make_concise',
      label: '⚡ Make Concise',
      icon: <Summarize />,
      description: 'Say more with fewer words',
      color: '#f59e0b',
      shortcut: 'C',
      endpoint: '/api/writing-assistant/make-concise'
    }
  ];

  // Enhanced API call to the new streamlined writing assistant endpoints
  const getAIAssistance = useCallback(async (text, actionType, customPrompt = '', context = '', intent = '') => {
    if (!text.trim()) return null;

    setIsLoading(true);
    
    try {
      // Find the mode configuration
      const mode = assistantModes.find(m => m.id === actionType);
      const endpoint = mode?.endpoint || '/api/writing-assistant/improve';
      
      // Call the new streamlined API
      const response = await api.post(endpoint, { content: text });
      
      // The response now contains only the improved text
      const improvedText = response.data.improvedText;
      
      return {
        suggestion: improvedText,
        confidence: 0.9, // High confidence since it's direct AI output
        explanation: `Applied ${mode?.label || actionType} enhancement`,
        originalLength: text.length,
        newLength: improvedText.length,
        endpoint: endpoint // For debugging
      };
    } catch (error) {
      console.error('Writing Assistant API error:', error);
      
      // Enhanced fallback with actual text processing
      let fallbackText = text;
      
      switch (actionType) {
        case 'improve':
        case 'enhance_clarity':
          fallbackText = text.replace(/\s+/g, ' ').trim();
          break;
        case 'grammar':
        case 'fix_grammar':
          fallbackText = text.replace(/\bi\b/g, 'I').replace(/\s+/g, ' ').trim();
          break;
        case 'tone':
        case 'adjust_tone':
          fallbackText = text.replace(/don't/g, 'do not').replace(/can't/g, 'cannot');
          break;
        case 'make_concise':
        case 'summarize':
          fallbackText = text.length > 100 ? text.substring(0, 100) + '...' : text;
          break;
        default:
          fallbackText = `Enhanced: ${text}`;
      }
      
      return {
        suggestion: fallbackText,
        confidence: 0.6,
        explanation: 'Fallback processing applied (API unavailable)',
        originalLength: text.length,
        newLength: fallbackText.length
      };
    } finally {
      setIsLoading(false);
    }
  }, [assistantModes]);

  // Handle assistant action with enhanced functionality
  const handleAssistantAction = async (mode) => {
    if (!selectedText.trim()) return;

    setIsLoading(true);
    setCurrentAction(mode);
    setIsEditing(false);

    try {
      const result = await getAIAssistance(
        selectedText, 
        mode,
        mode === 'custom' ? customPrompt : ''
      );
      
      if (result) {
        setAiResponse(result.suggestion);
        setEditedResponse(result.suggestion);
        setConfidence(result.confidence);
        
        // Add to history
        setActionHistory(prev => [{
          action: mode,
          timestamp: new Date(),
          originalText: selectedText.substring(0, 100),
          result: result.suggestion.substring(0, 100)
        }, ...prev.slice(0, 4)]); // Keep last 5 actions
        
        onSuggestion && onSuggestion(result.suggestion);
      }
    } catch (error) {
      console.error('Assistant error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle comprehensive document analysis
  const handleComprehensiveAnalysis = async () => {
    if (!selectedText.trim()) return;

    setIsLoading(true);
    setCurrentAction('comprehensive');
    setIsEditing(false);

    try {
      // Detect document context and intent
      const documentContext = detectDocumentContext(selectedText);
      const userIntent = detectUserIntent(selectedText);
      
      const result = await getAIAssistance(
        selectedText, 
        'comprehensive',
        '',
        documentContext,
        userIntent
      );
      
      if (result) {
        setAiResponse(result.suggestion);
        setEditedResponse(result.suggestion);
        setConfidence(result.confidence);
        
        // Add to history
        setActionHistory(prev => [{
          action: 'comprehensive',
          timestamp: new Date(),
          originalText: selectedText.substring(0, 100),
          result: result.suggestion.substring(0, 100),
          context: documentContext,
          intent: userIntent
        }, ...prev.slice(0, 4)]);
        
        onSuggestion && onSuggestion(result.suggestion);
      }
    } catch (error) {
      console.error('Comprehensive analysis error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Smart context detection
  const detectDocumentContext = (text) => {
    const lowerText = text.toLowerCase();
    
    if (lowerText.includes('email') || lowerText.includes('dear') || lowerText.includes('sincerely')) {
      return 'email';
    } else if (lowerText.includes('blog') || lowerText.includes('article')) {
      return 'blog post';
    } else if (lowerText.includes('technical') || lowerText.includes('documentation')) {
      return 'technical documentation';
    } else if (lowerText.includes('marketing') || lowerText.includes('product')) {
      return 'marketing content';
    } else if (lowerText.includes('academic') || lowerText.includes('research')) {
      return 'academic writing';
    }
    
    return 'general document';
  };

  // Smart intent detection
  const detectUserIntent = (text) => {
    const lowerText = text.toLowerCase();
    
    if (lowerText.includes('explain') || lowerText.includes('understand')) {
      return 'explain and inform';
    } else if (lowerText.includes('convince') || lowerText.includes('should')) {
      return 'persuade and convince';
    } else if (lowerText.includes('story') || lowerText.includes('imagine')) {
      return 'entertain and engage';
    } else if (lowerText.includes('how to') || lowerText.includes('steps')) {
      return 'instruct and guide';
    }
    
    return 'improve overall quality and clarity';
  };

  // Enhanced apply suggestion with animation support
  const applyEnhancedSuggestion = () => {
    if (editedResponse && onApplySuggestion) {
      // Call with just the text - the parent expects only the suggestion text
      onApplySuggestion(editedResponse);
      
      // Clear states after applying
      setAiResponse('');
      setEditedResponse('');
      setCurrentAction('');
      setIsEditing(false);
      setPreviewMode(false);
    }
  };

  // Dismiss suggestion
  const dismissSuggestion = () => {
    setAiResponse('');
    setEditedResponse('');
    setCurrentAction('');
    setIsEditing(false);
    setPreviewMode(false);
  };

  // Try again with same action
  const tryAgain = () => {
    if (currentAction) {
      handleAssistantAction(currentAction);
    }
  };

  // Copy suggestion to clipboard
  const copySuggestion = async () => {
    if (editedResponse) {
      try {
        await navigator.clipboard.writeText(editedResponse);
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    }
  };

  // Handle text formatting in editable response
  const applyFormatting = (format) => {
    if (editableRef.current) {
      document.execCommand(format, false, null);
      editableRef.current.focus();
    }
  };

  // Handle editable response change
  const handleEditableChange = (event) => {
    setEditedResponse(event.target.innerHTML);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        backgroundColor: theme.surface,
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        sx={{
          backgroundColor: theme.background,
          borderBottom: `1px solid ${theme.textSecondary}30`
        }}
      >
        <Box display="flex" alignItems="center">
          <AutoFixHigh sx={{ mr: 1, color: theme.primary }} />
          <Typography variant="h6" color={theme.text}>
            AI Writing Assistant
          </Typography>
          {selectedText && (
            <Chip
              label={`${selectedText.length} chars selected`}
              size="small"
              sx={{ ml: 2, backgroundColor: theme.primary, color: 'white' }}
            />
          )}
          {confidence > 0 && (
            <Box display="flex" alignItems="center" ml={2}>
              <Typography variant="caption" color={theme.textSecondary} mr={0.5}>
                Confidence:
              </Typography>
              <Rating
                value={confidence}
                max={1}
                precision={0.1}
                size="small"
                readOnly
                sx={{ color: theme.primary }}
              />
              <Typography variant="caption" color={theme.textSecondary} ml={0.5}>
                {Math.round(confidence * 100)}%
              </Typography>
            </Box>
          )}
        </Box>
        
        <Box display="flex" alignItems="center" gap={1}>
          {actionHistory.length > 0 && (
            <Tooltip title="Recent actions">
              <IconButton size="small" sx={{ color: theme.textSecondary }}>
                <History />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{ color: theme.text }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Collapse in={isExpanded}>
        <Box p={3}>
          {!selectedText ? (
            <Alert
              severity="info"
              sx={{
                backgroundColor: `${theme.primary}15`,
                color: theme.text,
                '& .MuiAlert-icon': { color: theme.primary }
              }}
            >
              Select some text in the editor to get AI-powered writing assistance!
            </Alert>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Selected Text Context */}
              {selectedText && (
                <Box mb={2}>
                  <Typography variant="caption" color={theme.textSecondary} mb={1} display="block">
                    Selected Text ({selectedText.length} chars):
                  </Typography>
                  <Paper
                    sx={{
                      p: 1.5,
                      backgroundColor: `${theme.primary}10`,
                      border: `1px solid ${theme.primary}30`,
                      borderRadius: 1,
                      maxHeight: '80px',
                      overflow: 'auto'
                    }}
                  >
                    <Typography variant="body2" color={theme.text} sx={{ fontSize: '0.875rem' }}>
                      "{selectedText.length > 100 ? selectedText.substring(0, 100) + '...' : selectedText}"
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Enhanced Assistant Modes */}
              <Box mb={3}>
                <Typography variant="subtitle2" color={theme.textSecondary} mb={2}>
                  Choose an action:
                </Typography>
                
                <Box 
                  display="grid" 
                  gridTemplateColumns="repeat(auto-fit, minmax(140px, 1fr))" 
                  gap={1.5}
                  mb={2}
                >
                  {assistantModes.map((mode) => (
                    <motion.div
                      key={mode.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Tooltip title={mode.description} placement="top">
                        <Button
                          variant={assistantMode === mode.id ? 'contained' : 'outlined'}
                          size="small"
                          startIcon={isLoading && currentAction === mode.id ? 
                            <CircularProgress size={16} color="inherit" /> : mode.icon
                          }
                          onClick={() => {
                            setAssistantMode(mode.id);
                            handleAssistantAction(mode.id);
                          }}
                          disabled={isLoading}
                          sx={{
                            minHeight: '44px',
                            backgroundColor: assistantMode === mode.id ? mode.color : 'transparent',
                            borderColor: mode.color,
                            color: assistantMode === mode.id ? 'white' : mode.color,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                              backgroundColor: assistantMode === mode.id ? 
                                `${mode.color}dd` : `${mode.color}15`,
                              borderColor: mode.color,
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${mode.color}30`
                            },
                            '&:disabled': {
                              opacity: 0.6
                            }
                          }}
                          fullWidth
                        >
                          {mode.label}
                        </Button>
                      </Tooltip>
                    </motion.div>
                  ))}
                </Box>

                {/* Quick Actions */}
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<AutoAwesome />}
                    onClick={() => handleAssistantAction('improve')}
                    disabled={isLoading || !selectedText}
                    sx={{ 
                      color: theme.primary,
                      fontSize: '0.75rem',
                      textTransform: 'none'
                    }}
                  >
                    Quick Fix
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Spellcheck />}
                    onClick={() => handleAssistantAction('grammar')}
                    disabled={isLoading || !selectedText}
                    sx={{ 
                      color: '#10b981',
                      fontSize: '0.75rem',
                      textTransform: 'none'
                    }}
                  >
                    Grammar Check
                  </Button>
                </Box>
              </Box>

              {/* Custom Prompt */}
              <Box mb={3}>
                <Typography variant="subtitle2" color={theme.textSecondary} mb={1}>
                  Or use a custom prompt:
                </Typography>
                
                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g., Make this more persuasive, Add humor, etc."
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme.background,
                        '& fieldset': { borderColor: `${theme.textSecondary}30` },
                        '&:hover fieldset': { borderColor: theme.primary },
                        '&.Mui-focused fieldset': { borderColor: theme.primary }
                      },
                      '& .MuiInputBase-input': { color: theme.text },
                      '& .MuiInputBase-input::placeholder': { 
                        color: theme.textSecondary,
                        opacity: 0.7
                      }
                    }}
                  />
                  
                  <Button
                    variant="contained"
                    disabled={!customPrompt.trim() || isLoading}
                    onClick={() => handleAssistantAction('custom')}
                    sx={{ backgroundColor: theme.primary }}
                  >
                    {isLoading ? <CircularProgress size={20} /> : 'Apply'}
                  </Button>
                </Box>
              </Box>

              {/* Enhanced AI Suggestion with Editable Response */}
              <AnimatePresence>
                {aiResponse && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Divider sx={{ my: 2, borderColor: `${theme.textSecondary}20` }} />
                    
                    <Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle2" color={theme.textSecondary}>
                          AI Suggestion:
                        </Typography>
                        
                        <Box display="flex" gap={1} alignItems="center">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={previewMode}
                                onChange={(e) => setPreviewMode(e.target.checked)}
                                size="small"
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: theme.primary,
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: theme.primary,
                                  },
                                }}
                              />
                            }
                            label={
                              <Box display="flex" alignItems="center" gap={0.5}>
                                {previewMode ? <Visibility /> : <VisibilityOff />}
                                <Typography variant="caption">Preview</Typography>
                              </Box>
                            }
                            sx={{ 
                              '& .MuiFormControlLabel-label': { 
                                color: theme.textSecondary,
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                          
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setIsEditing(!isEditing)}
                            startIcon={<Edit />}
                            sx={{ 
                              color: theme.primary,
                              fontSize: '0.75rem',
                              textTransform: 'none'
                            }}
                          >
                            {isEditing ? 'View' : 'Edit'}
                          </Button>
                        </Box>
                      </Box>

                      {/* Preview Mode - Side by Side Comparison */}
                      {previewMode && (
                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                          <Box>
                            <Typography variant="caption" color={theme.textSecondary} mb={1} display="block">
                              Original:
                            </Typography>
                            <Paper
                              sx={{
                                p: 2,
                                backgroundColor: `${theme.error}10`,
                                border: `1px solid ${theme.error}30`,
                                maxHeight: '120px',
                                overflow: 'auto',
                                borderRadius: 2
                              }}
                            >
                              <Typography variant="body2" color={theme.text} sx={{ fontSize: '0.875rem' }}>
                                {selectedText}
                              </Typography>
                            </Paper>
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" color={theme.textSecondary} mb={1} display="block">
                              Suggested:
                            </Typography>
                            <Paper
                              sx={{
                                p: 2,
                                backgroundColor: `${theme.success}10`,
                                border: `1px solid ${theme.success}30`,
                                maxHeight: '120px',
                                overflow: 'auto',
                                borderRadius: 2
                              }}
                            >
                              <Typography variant="body2" color={theme.text} sx={{ fontSize: '0.875rem' }}>
                                {editedResponse}
                              </Typography>
                            </Paper>
                          </Box>
                        </Box>
                      )}

                      {/* Editable Response */}
                      {!previewMode && (
                        <Box>
                          {/* Formatting Controls */}
                          <Collapse in={isEditing && showFormatting}>
                            <Box display="flex" gap={1} mb={1} p={1} 
                                 sx={{ backgroundColor: `${theme.background}50`, borderRadius: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => applyFormatting('bold')}
                                sx={{ color: theme.textSecondary }}
                              >
                                <FormatBold fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => applyFormatting('italic')}
                                sx={{ color: theme.textSecondary }}
                              >
                                <FormatItalic fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => applyFormatting('underline')}
                                sx={{ color: theme.textSecondary }}
                              >
                                <FormatUnderlined fontSize="small" />
                              </IconButton>
                            </Box>
                          </Collapse>

                          {isEditing ? (
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              maxRows={8}
                              value={editedResponse}
                              onChange={(e) => setEditedResponse(e.target.value)}
                              placeholder="Edit the AI suggestion..."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: `${theme.success}08`,
                                  border: `1px solid ${theme.success}30`,
                                  borderRadius: 2,
                                  '& fieldset': { border: 'none' },
                                  '&:hover': { 
                                    backgroundColor: `${theme.success}12`,
                                    border: `1px solid ${theme.success}50`
                                  },
                                  '&.Mui-focused': { 
                                    backgroundColor: `${theme.success}15`,
                                    border: `1px solid ${theme.success}70`
                                  }
                                },
                                '& .MuiInputBase-input': { 
                                  color: theme.text,
                                  fontSize: '0.875rem',
                                  lineHeight: 1.5
                                },
                                mb: 2
                              }}
                            />
                          ) : (
                            <Paper
                              sx={{
                                p: 2,
                                backgroundColor: `${theme.success}10`,
                                border: `1px solid ${theme.success}30`,
                                maxHeight: '200px',
                                overflow: 'auto',
                                mb: 2,
                                borderRadius: 2,
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: `${theme.success}15`,
                                  border: `1px solid ${theme.success}50`
                                }
                              }}
                              onClick={() => setIsEditing(true)}
                            >
                              <Typography variant="body2" color={theme.text} sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                                {editedResponse}
                              </Typography>
                              
                              {editedResponse !== aiResponse && (
                                <Chip
                                  label="Modified"
                                  size="small"
                                  sx={{ 
                                    mt: 1,
                                    backgroundColor: theme.primary,
                                    color: 'white',
                                    fontSize: '0.7rem'
                                  }}
                                />
                              )}
                            </Paper>
                          )}

                          {/* Character count and stats */}
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="caption" color={theme.textSecondary}>
                              {editedResponse.length} characters
                              {selectedText && (
                                <span>
                                  {' • '}
                                  {editedResponse.length > selectedText.length ? '+' : ''}
                                  {editedResponse.length - selectedText.length} change
                                </span>
                              )}
                            </Typography>
                            
                            {isEditing && (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => setShowFormatting(!showFormatting)}
                                sx={{ 
                                  color: theme.textSecondary,
                                  fontSize: '0.7rem',
                                  textTransform: 'none'
                                }}
                              >
                                {showFormatting ? 'Hide' : 'Show'} Formatting
                              </Button>
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* Enhanced Action Buttons */}
                      <Box 
                        display="flex" 
                        gap={2} 
                        justifyContent="space-between" 
                        alignItems="center"
                        sx={{ mt: 2 }}
                      >
                        {/* Primary Actions */}
                        <Box display="flex" gap={1.5}>
                          {/* Enhanced Apply Button */}
                          <motion.div
                            whileHover={{ scale: 1.02, y: -1 }}
                            whileTap={{ scale: 0.98 }}
                            style={{ cursor: 'pointer' }}
                            onClick={applyEnhancedSuggestion}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                padding: '12px 24px',
                                background: `linear-gradient(135deg, 
                                  rgba(76, 175, 80, 0.15) 0%, 
                                  rgba(76, 175, 80, 0.05) 100%)`,
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                border: '1px solid rgba(76, 175, 80, 0.2)',
                                borderRadius: '12px',
                                color: '#4CAF50',
                                fontWeight: 600,
                                fontSize: '14px',
                                minWidth: '120px',
                                height: '44px',
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: '0 4px 20px rgba(76, 175, 80, 0.1)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  background: `linear-gradient(135deg, 
                                    rgba(76, 175, 80, 0.25) 0%, 
                                    rgba(76, 175, 80, 0.1) 100%)`,
                                  border: '1px solid rgba(76, 175, 80, 0.4)',
                                  boxShadow: '0 8px 30px rgba(76, 175, 80, 0.2)',
                                  transform: 'translateY(-2px)',
                                },
                                '&:active': {
                                  transform: 'translateY(0px)',
                                }
                              }}
                            >
                              <KeyboardArrowLeft 
                                sx={{ 
                                  fontSize: '18px',
                                  color: '#4CAF50'
                                }} 
                              />
                              <span style={{ fontFamily: 'inherit', fontWeight: 600 }}>
                                Apply Changes
                              </span>
                            </Box>
                          </motion.div>

                          {/* Enhanced Dismiss Button */}
                          <motion.div
                            whileHover={{ scale: 1.02, y: -1 }}
                            whileTap={{ scale: 0.98 }}
                            style={{ cursor: 'pointer' }}
                            onClick={dismissSuggestion}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                padding: '12px 20px',
                                background: `linear-gradient(135deg, 
                                  rgba(158, 158, 158, 0.1) 0%, 
                                  rgba(158, 158, 158, 0.05) 100%)`,
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                border: '1px solid rgba(158, 158, 158, 0.2)',
                                borderRadius: '12px',
                                color: '#9E9E9E',
                                fontWeight: 500,
                                fontSize: '14px',
                                minWidth: '100px',
                                height: '44px',
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: '0 2px 15px rgba(158, 158, 158, 0.08)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  background: `linear-gradient(135deg, 
                                    rgba(244, 67, 54, 0.15) 0%, 
                                    rgba(244, 67, 54, 0.05) 100%)`,
                                  border: '1px solid rgba(244, 67, 54, 0.3)',
                                  color: '#F44336',
                                  boxShadow: '0 6px 25px rgba(244, 67, 54, 0.15)',
                                  transform: 'translateY(-2px)',
                                },
                                '&:active': {
                                  transform: 'translateY(0px)',
                                }
                              }}
                            >
                              <Close 
                                sx={{ 
                                  fontSize: '16px'
                                }} 
                              />
                              <span style={{ fontFamily: 'inherit', fontWeight: 500 }}>
                                Dismiss
                              </span>
                            </Box>
                          </motion.div>
                        </Box>

                        {/* Secondary Actions */}
                        <Box display="flex" gap={1}>
                          {/* Try Again */}
                          <motion.div
                            whileHover={{ scale: 1.05, rotate: 90 }}
                            whileTap={{ scale: 0.95 }}
                            style={{ cursor: 'pointer' }}
                            onClick={tryAgain}
                          >
                            <Tooltip title="Try again" placement="top">
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  background: `linear-gradient(135deg, 
                                    rgba(33, 150, 243, 0.15) 0%, 
                                    rgba(33, 150, 243, 0.05) 100%)`,
                                  backdropFilter: 'blur(8px)',
                                  WebkitBackdropFilter: 'blur(8px)',
                                  border: '1px solid rgba(33, 150, 243, 0.2)',
                                  borderRadius: '12px',
                                  color: '#2196F3',
                                  boxShadow: '0 2px 12px rgba(33, 150, 243, 0.1)',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  opacity: isLoading ? 0.5 : 1,
                                  pointerEvents: isLoading ? 'none' : 'auto',
                                  '&:hover': {
                                    background: `linear-gradient(135deg, 
                                      rgba(33, 150, 243, 0.25) 0%, 
                                      rgba(33, 150, 243, 0.1) 100%)`,
                                    border: '1px solid rgba(33, 150, 243, 0.4)',
                                    boxShadow: '0 4px 20px rgba(33, 150, 243, 0.2)',
                                  }
                                }}
                              >
                                <Refresh sx={{ fontSize: '18px' }} />
                              </Box>
                            </Tooltip>
                          </motion.div>
                          
                          {/* Copy */}
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            style={{ cursor: 'pointer' }}
                            onClick={copySuggestion}
                          >
                            <Tooltip title="Copy suggestion" placement="top">
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  background: `linear-gradient(135deg, 
                                    rgba(158, 158, 158, 0.1) 0%, 
                                    rgba(158, 158, 158, 0.05) 100%)`,
                                  backdropFilter: 'blur(8px)',
                                  WebkitBackdropFilter: 'blur(8px)',
                                  border: '1px solid rgba(158, 158, 158, 0.15)',
                                  borderRadius: '12px',
                                  color: '#9E9E9E',
                                  boxShadow: '0 2px 12px rgba(158, 158, 158, 0.08)',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    background: `linear-gradient(135deg, 
                                      rgba(156, 39, 176, 0.15) 0%, 
                                      rgba(156, 39, 176, 0.05) 100%)`,
                                    border: '1px solid rgba(156, 39, 176, 0.3)',
                                    color: '#9C27B0',
                                    boxShadow: '0 4px 20px rgba(156, 39, 176, 0.15)',
                                  }
                                }}
                              >
                                <ContentCopy sx={{ fontSize: '16px' }} />
                              </Box>
                            </Tooltip>
                          </motion.div>

                          {/* Feedback Buttons */}
                          <Box display="flex" gap={0.5} ml={1}>
                            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                              <Tooltip title="Good suggestion">
                                <IconButton
                                  size="small"
                                  sx={{ 
                                    color: theme.success,
                                    '&:hover': { backgroundColor: `${theme.success}15` }
                                  }}
                                >
                                  <ThumbUp fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </motion.div>
                            
                            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                              <Tooltip title="Poor suggestion">
                                <IconButton
                                  size="small"
                                  sx={{ 
                                    color: theme.error,
                                    '&:hover': { backgroundColor: `${theme.error}15` }
                                  }}
                                >
                                  <ThumbDown fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </motion.div>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Action History */}
                    {actionHistory.length > 0 && (
                      <Fade in={true}>
                        <Box mt={3}>
                          <Divider sx={{ mb: 2, borderColor: `${theme.textSecondary}20` }} />
                          <Typography variant="caption" color={theme.textSecondary} mb={1} display="block">
                            Recent Actions:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {actionHistory.slice(0, 3).map((action, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                              >
                                <Chip
                                  label={`${assistantModes.find(m => m.id === action.action)?.label || action.action} • ${action.originalText}...`}
                                  size="small"
                                  onClick={() => handleAssistantAction(action.action)}
                                  sx={{
                                    backgroundColor: `${theme.primary}10`,
                                    color: theme.textSecondary,
                                    fontSize: '0.7rem',
                                    maxWidth: '200px',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      backgroundColor: `${theme.primary}20`,
                                      color: theme.primary
                                    }
                                  }}
                                />
                              </motion.div>
                            ))}
                          </Box>
                        </Box>
                      </Fade>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default WritingAssistant;
